import React, { useCallback, useMemo } from 'react';
import { buildDateRangeListOptions, DATE_RANGE_MODES, DateRangeMode } from './validation/dateRangeModesConfig';
import { MONTH_NAMES } from '../components/validation/ValidInputDate.utils';
import ValidInputDate, { validPropDateVM } from './validation/ValidInputDate';
import ValidInputDroplistText, { validPropDroplistTextVM } from './validation/ValidInputDroplistText';
import { ValidOption } from './validation/types';
import utilSvc from '../utilSvc';

const monthOptions = MONTH_NAMES.map((name, i) => ({ lbl: name, val: i + 1 }));

const getYearOptions = (): ValidOption[] => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => ({ lbl: String(thisYear - i), val: thisYear - i }));
};

const quarterOptions = [
    { lbl: 'Q1 (Jan - Mar)', val: 1 },
    { lbl: 'Q2 (Apr - Jun)', val: 2 },
    { lbl: 'Q3 (Jul - Sep)', val: 3 },
    { lbl: 'Q4 (Oct - Dec)', val: 4 },
];

type DateRangeDropdownProps = {
    value: string;
    onChange: (val: string) => void;
    allowedModes?: DateRangeMode[];
    disabled?: boolean;
};

const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
    value,
    onChange,
    allowedModes = DATE_RANGE_MODES,
    disabled,
}) => {
    const allOptions = buildDateRangeListOptions();
    const options = allowedModes
        ? allOptions.filter(opt => allowedModes.includes(opt.val as DateRangeMode))
        : allOptions;
    const prop = useMemo(
        () => validPropDroplistTextVM('DateRangeMode', 'Date Range', value, '', options),
        [value, options],
    );
    return (
        <ValidInputDroplistText
            prop={prop}
            onChange={(_, val) => onChange(val as string)}
            disabled={disabled}
            customMaxWidth={240}
        />
    );
};

function MonthlyDropdowns({
    month,
    year,
    onChange,
    disabled,
}: {
    month: number;
    year: number;
    onChange: (m: number, y: number) => void;
    disabled?: boolean;
}): JSX.Element {
    const monthProp = validPropDroplistTextVM('MonthlyMonth', 'Month', month, '', monthOptions);
    const yearProp = validPropDroplistTextVM('MonthlyYear', 'Year', year, '', getYearOptions());

    const handleMonthChange = (_: any, val: any): void => {
        onChange(Number(val), year);
    };
    const handleYearChange = (_: any, val: any): void => {
        onChange(month, Number(val));
    };
    return (
        <>
            <ValidInputDroplistText
                prop={monthProp}
                onChange={handleMonthChange}
                noMinWidth
                disabled={disabled}
            />
            <ValidInputDroplistText
                prop={yearProp}
                onChange={handleYearChange}
                noMinWidth
                disabled={disabled}
            />
        </>
    );
}

function QuarterDropdowns({
    quarter,
    year,
    onChange,
    disabled,
}: {
    quarter: number;
    year: number;
    onChange: (q: number, y: number) => void;
    disabled?: boolean;
}): JSX.Element {
    const quarterProp = validPropDroplistTextVM('Quarter', 'Quarter', quarter, '', quarterOptions);
    const yearProp = validPropDroplistTextVM('QuarterYear', 'Year', year, '', getYearOptions());
    return (
        <>
            <ValidInputDroplistText
                prop={quarterProp}
                onChange={(_, val) => onChange(Number(val), year)}
                noMinWidth
                disabled={disabled}
            />
            <ValidInputDroplistText
                prop={yearProp}
                onChange={(_, val) => onChange(quarter, Number(val))}
                noMinWidth
                disabled={disabled}
            />
        </>
    );
}

type DateRangeControllerProps = {
    mode: string;
    start: Date;
    end: Date;
    monthlyMonth: number;
    monthlyYear: number;
    qtr: number;
    qtrYear: number;
    allowedModes?: DateRangeMode[];
    onModeChange: (mode: string) => void;
    onStartChange: (date: Date) => void;
    onEndChange: (date: Date) => void;
    onMonthlyChange: (month: number, year: number) => void;
    onQuarterChange: (qtr: number, year: number) => void;
    startError?: string;
    endError?: string;
    disabled?: boolean;
};

const DateRangeController: React.FC<DateRangeControllerProps> = ({
    mode,
    start,
    end,
    monthlyMonth,
    monthlyYear,
    qtr,
    qtrYear,
    allowedModes,
    onModeChange,
    onStartChange,
    onEndChange,
    onMonthlyChange,
    onQuarterChange,
    startError,
    endError,
    disabled,
}) => {
    const isFixedMode = mode !== 'CUSTOM3' && mode !== 'CUSTOM92';

    const handleStartChange = useCallback(
        (_, val) => {
            const [year, month, day] = val.split('-').map(Number);
            const d = new Date(year, month - 1, day);
            onStartChange(d);
        },
        [onStartChange],
    );

    const handleEndChange = useCallback(
        (_, val) => {
            const [year, month, day] = val.split('-').map(Number);
            const d = new Date(year, month - 1, day);
            onEndChange(d);
        },
        [onEndChange],
    );

    return (
        <div className="date-range-controller-component">
            <DateRangeDropdown
                value={mode}
                onChange={onModeChange}
                allowedModes={allowedModes}
                disabled={disabled}
            />
            {mode === 'MONTHLY' && (
                <MonthlyDropdowns
                    month={monthlyMonth}
                    year={monthlyYear}
                    onChange={onMonthlyChange}
                    disabled={disabled}
                />
            )}
            {mode === 'QTR' && (
                <QuarterDropdowns
                    quarter={qtr}
                    year={qtrYear}
                    onChange={onQuarterChange}
                    disabled={disabled}
                />
            )}
            {mode !== 'MONTHLY' && mode !== 'QTR' && (
                <div className="date-range-inline-fields">
                    <ValidInputDate
                        key={`start-${utilSvc.dt.isValid(start) ? start.toISOString().slice(0, 10) : ''}`}
                        prop={{
                            ...validPropDateVM(
                                'StartDate',
                                'Start Date',
                                utilSvc.dt.isValid(start) ? start.toISOString().slice(0, 10) : '',
                            ),
                            err: startError || '',
                        }}
                        onChange={handleStartChange}
                        disabled={disabled || isFixedMode}
                    />
                    <ValidInputDate
                        key={`end-${utilSvc.dt.isValid(end) ? end.toISOString().slice(0, 10) : ''}`}
                        prop={{
                            ...validPropDateVM(
                                'EndDate',
                                'End Date',
                                utilSvc.dt.isValid(end) ? end.toISOString().slice(0, 10) : '',
                            ),
                            err: endError || '',
                        }}
                        onChange={handleEndChange}
                        disabled={disabled || isFixedMode}
                    />
                </div>
            )}
        </div>
    );
};

export default DateRangeController;
